<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
  >
    <v-card
      v-if="formLoading"
      class="d-flex justify-center"
    >
      <v-progress-circular
        :size="80"
        :width="5"
        color="primary"
        indeterminate
      />
    </v-card>

    <v-card v-else>
      <v-card-title>
        <span class="headline">{{ isEdite ? 'Update' : 'Add' }} Payin Fee</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.sourceCountryCode"
                :items="sourceCountryItems"
                label="Source country"
                item-value="codeIso3"
                :item-text="i => `${i.codeIso3}: ${i.name}`"
                :disabled="isEdite"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.sourceCurrencyCode"
                :items="currencyItems"
                label="Source Currency"
                :disabled="isEdite || cascadeEnabled"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="formData.payInMethod"
                :items="payInMethodItems"
                item-text="label"
                item-value="value"
                label="Payment Method"
                :disabled="isEdite"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="formData.paymentProvider"
                :items="paymentProviderItems"
                item-text="label"
                item-value="value"
                label="Payment Provider"
                :disabled="isEdite"
              />
            </v-col>
            <v-col>
              <h4 class="text-h6">
                Fee Pricing Range
              </h4>
            </v-col>
            <v-row>
              <v-card
                v-for="(item, index) in feesRange"
                :key="`payin-fees-range-item-${index}`"
                class="card-fee-range"
                outlined
                shaped
              >
                <v-card-text>
                  <v-row>
                    <v-btn
                      v-if="index > 0"
                      class="card-fee-range__remove"
                      icon
                      outlined
                      plain
                      fab
                      x-small
                      @click="onRemoveFeeRangeItem(index)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-col cols="12">
                      <v-radio-group
                        v-model="item.type"
                        row
                      >
                        <v-radio
                          label="Fixed Fee"
                          value="fixed"
                        />
                        <v-radio
                          label="Percentage Fee"
                          value="percentage"
                        />
                      </v-radio-group>
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                    >
                      <v-text-field
                        v-model="item.minAmount"
                        type="number"
                        label="Min Amount value"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                    >
                      <v-text-field
                        v-model="item.value"
                        type="number"
                        :label="item.type === 'percentage' ? 'Value (Percentage fee rate)' : 'Value'"
                        :suffix="item.type === 'percentage' ? '%' : ''"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-row>
            <v-btn
              v-if="feesRange.length < feesRangeLimit"
              class="mt-2 primary"
              block
              @click="onAddFeeRange"
            >
              <v-icon class="mr-2">
                mdi-plus
              </v-icon>
              Add fee range
            </v-btn>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn @click="onClose">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading"
          @click="onSubmit"
        >
          {{ isEdite ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// utils
import { rateCurrencies } from "@/utils/staticAutopricing";
import {
  payInMethodOptions,
  paymentProviderOptions,
  hasPayinOptions,
  payinSourceCountries,
  payinCurrencyFor,
  payinMethodOptionsFor,
  payinProviderOptionsFor
} from "@/utils/staticPayinFeeOptions";

export default {
  name: "CreatePayinFeeModal",

  props: {
    value: {
      type: Boolean,
      default: false
    },
    editedItem: {
      type: Object,
      default: () => null
    }
  },

  data: () => ({
    isEdite: false,
    formLoading: false,
    loading: false,
    currencies: rateCurrencies,
    payInMethodOptions,
    paymentProviderOptions,
    formData: {
      sourceCountryCode: '',
      sourceCurrencyCode: '',
      payInMethod: '',
      paymentProvider: ''
    },
    feesRange: [],
    feesRangeLimit: 50
  }),

  computed: {
    listSourceCountries() {
      return this.$store.getters["ActiveCountryStore/getListSourceCountries"];
    },
    tenant() {
      return this.$store.getters["AppStore/getTenant"];
    },
    // Create mode narrows every dropdown to what the tenant's middleware actually offers.
    // Edit mode keeps the flat lists so existing rows always render, whatever they hold.
    cascadeEnabled() {
      return !this.isEdite && hasPayinOptions(this.tenant);
    },
    sourceCountryItems() {
      return this.cascadeEnabled
        ? payinSourceCountries(this.tenant)
        : this.listSourceCountries;
    },
    currencyItems() {
      if (!this.cascadeEnabled) return this.currencies;
      const currency = payinCurrencyFor(this.tenant, this.formData.sourceCountryCode);
      return currency ? [currency] : [];
    },
    payInMethodItems() {
      return this.cascadeEnabled
        ? payinMethodOptionsFor(this.tenant, this.formData.sourceCountryCode)
        : this.payInMethodOptions;
    },
    paymentProviderItems() {
      return this.cascadeEnabled
        ? payinProviderOptionsFor(
          this.tenant,
          this.formData.sourceCountryCode,
          this.formData.payInMethod
        )
        : this.paymentProviderOptions;
    },
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    filterStringPayload() {
      const filter = this.editedItem;
      return `sourceCountryCode:${ filter.sourceCountryCode },sourceCurrencyCode:${ filter.sourceCurrencyCode },payInMethod:${ filter.payInMethod },paymentProvider:${ filter.paymentProvider }`;
    }
  },

  watch: {
    "formData.sourceCountryCode"(codeIso3) {
      if (!this.cascadeEnabled) return;
      this.formData.sourceCurrencyCode = payinCurrencyFor(this.tenant, codeIso3) || "";
      this.formData.payInMethod = "";
      this.formData.paymentProvider = "";
    },
    "formData.payInMethod"() {
      if (!this.cascadeEnabled) return;
      const allowed = this.paymentProviderItems.map(option => option.value);
      if (allowed.includes(this.formData.paymentProvider)) return;
      this.formData.paymentProvider = allowed.length === 1 ? allowed[0] : "";
    }
  },

  created() {
    if (this.editedItem) {
      this.isEdite = true;
      this.getPrefilledFees();
    } else {
      this.feesRange.push({
        type: 'fixed',
        minAmount: 0,
        value: 0
      });
    }
  },

  methods: {
    async getPrefilledFees () {
      this.formLoading = true;

      await this.$store
        .dispatch("PayinFeesStore/getPrefilledFees", {
          page: 1,
          size: 50,
          filter: this.filterStringPayload
        })
        .then((res) => {
          const data = res.list;
          this.onPrefillFormData(data);
        })
        .finally(() => this.formLoading = false);
    },

    onPrefillFormData (data) {
      if (data && data.length) {
        this.$set(this.formData, 'sourceCountryCode', data[0].sourceCountryCode);
        this.$set(this.formData, 'sourceCurrencyCode', data[0].sourceCurrencyCode);
        this.$set(this.formData, 'payInMethod', data[0].payInMethod);
        this.$set(this.formData, 'paymentProvider', data[0].paymentProvider);

        data.forEach((item) => {
          this.feesRange.push({
            type: item.type,
            minAmount: item.minAmount,
            value: item.value
          });
        });
      }
    },

    onRemoveFeeRangeItem (index) {
      this.$delete(this.feesRange, index);
    },

    onAddFeeRange () {
      this.feesRange.push({
        type: 'fixed',
        minAmount: 0,
        value: 0
      });
    },

    onSubmit () {
      this.loading = true;
      const data = {
        fees: []
      };

      if (this.feesRange && this.feesRange.length) {
        this.feesRange.forEach((item) => {
          data.fees.push({
            ...this.formData,
            ...item
          });
        });
      }

      this.$store
        .dispatch("PayinFeesStore/createFees", data)
        .then(() => {
          this.$emit("on-create");
          this.onClose();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onClose () {
      this.dialog = false;
    }
  }
};
</script>

<style lang="scss">
.card-fee-range {
  position: relative;
  margin-bottom: 1rem;
  border-color: #ddd;

  &__remove {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 2;
  }

  .v-input {
    margin: 0;
  }

  .v-input__slot {
    margin-bottom: 0;
  }
}

.v-progress-circular {
  margin: 3rem auto;
}
</style>
